const DB_NAME = "ScreenRecRecoveryDB";
const DB_VERSION = 1;

let dbInstance = null;

function initDb() {
  if (dbInstance) return Promise.resolve(dbInstance);

  return new Promise((resolve, reject) => {
    try {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = (event) => {
        console.error("IndexedDB error:", event.target.error);
        reject(event.target.error);
      };

      request.onsuccess = (event) => {
        dbInstance = event.target.result;
        resolve(dbInstance);
      };

      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        if (!db.objectStoreNames.contains("metadata")) {
          db.createObjectStore("metadata", { keyPath: "id" });
        }
        if (!db.objectStoreNames.contains("chunks")) {
          db.createObjectStore("chunks", { keyPath: "id" });
        }
      };
    } catch (e) {
      console.error("IndexedDB not supported or blocked", e);
      reject(e);
    }
  });
}

export const recordingStorage = {
  async startNewSession(mime, filename) {
    try {
      const db = await initDb();
      const sessionId = `rec_${Date.now()}`;

      // Clear old data first
      await this.clearStorage();

      return new Promise((resolve, reject) => {
        const tx = db.transaction(["metadata"], "readwrite");
        const store = tx.objectStore("metadata");
        const metadata = {
          id: "session",
          sessionId,
          mime,
          filename,
          status: "recording",
          timestamp: Date.now(),
        };
        const request = store.put(metadata);

        request.onsuccess = () => resolve(sessionId);
        request.onerror = (e) => reject(e.target.error);
      });
    } catch (error) {
      console.error("Failed to start new session in IndexedDB", error);
      return null;
    }
  },

  async appendChunk(sessionId, index, blob) {
    try {
      const db = await initDb();
      return new Promise((resolve, reject) => {
        const tx = db.transaction(["chunks"], "readwrite");
        const store = tx.objectStore("chunks");
        const chunkRecord = {
          id: `${sessionId}_${index}`,
          sessionId,
          index,
          blob,
        };
        const request = store.put(chunkRecord);
        request.onsuccess = () => resolve(true);
        request.onerror = (e) => reject(e.target.error);
      });
    } catch (error) {
      console.error("Failed to append chunk to IndexedDB", error);
      throw error;
    }
  },

  async markSessionCompleted(sessionId) {
    try {
      const db = await initDb();
      return new Promise((resolve, reject) => {
        const tx = db.transaction(["metadata"], "readwrite");
        const store = tx.objectStore("metadata");
        const getReq = store.get("session");

        getReq.onsuccess = () => {
          const metadata = getReq.result;
          if (metadata && metadata.sessionId === sessionId) {
            metadata.status = "completed";
            const putReq = store.put(metadata);
            putReq.onsuccess = () => resolve();
            putReq.onerror = (e) => reject(e.target.error);
          } else {
            resolve();
          }
        };
        getReq.onerror = (e) => reject(e.target.error);
      });
    } catch (error) {
      console.error("Failed to mark session completed in IndexedDB", error);
    }
  },

  async getCurrentSession() {
    try {
      const db = await initDb();
      return new Promise((resolve, reject) => {
        const tx = db.transaction(["metadata"], "readonly");
        const store = tx.objectStore("metadata");
        const request = store.get("session");
        request.onsuccess = () => resolve(request.result || null);
        request.onerror = (e) => reject(e.target.error);
      });
    } catch (error) {
      console.error("Failed to get current session from IndexedDB", error);
      return null;
    }
  },

  async getSessionChunks(sessionId) {
    try {
      const db = await initDb();
      return new Promise((resolve, reject) => {
        const tx = db.transaction(["chunks"], "readonly");
        const store = tx.objectStore("chunks");

        const request = store.getAll();
        request.onsuccess = () => {
          const allChunks = request.result || [];
          const filtered = allChunks
            .filter((c) => c.sessionId === sessionId)
            .sort((a, b) => a.index - b.index)
            .map((c) => c.blob);
          resolve(filtered);
        };
        request.onerror = (e) => reject(e.target.error);
      });
    } catch (error) {
      console.error("Failed to get session chunks from IndexedDB", error);
      return [];
    }
  },

  async clearStorage() {
    try {
      const db = await initDb();
      return new Promise((resolve, reject) => {
        const tx = db.transaction(["metadata", "chunks"], "readwrite");
        const metaStore = tx.objectStore("metadata");
        const chunksStore = tx.objectStore("chunks");

        metaStore.clear();
        chunksStore.clear();

        tx.oncomplete = () => resolve();
        tx.onerror = (e) => reject(e.target.error);
      });
    } catch (error) {
      console.error("Failed to clear IndexedDB storage", error);
    }
  },
};
